import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MTM_AUTH_CLIENT, MtmAuthBridgeService } from '@mtm/auth-ui-angular';
import type { DriveAuthContext } from '@mtm/drive/core';

/**
 * Supplies the Drive library's auth/tenant context from the IAM auth SDK that
 * mtm-webapp already wires via `provideMtmAuth()`. Aliased to `DRIVE_AUTH_CONTEXT`
 * with `useExisting` so the library reads the same singleton the app refreshes.
 */
@Injectable({ providedIn: 'root' })
export class DriveAuthContextService implements DriveAuthContext {
  private readonly _auth = inject(MTM_AUTH_CLIENT);
  private readonly _bridge = inject(MtmAuthBridgeService);

  readonly tenantId = signal<number>(0);
  readonly tenantUuid = signal<string | null>(null);
  // Reactive account identity: tracks the bridge's signed-in user so it flips to
  // null on logout and to the new uuid on login. The drive caches key their
  // reset on this, so a user switch wipes the previous account's cached previews.
  readonly accountUuid = computed<string | null>(() => this._bridge.currentUser()?.accountUuid ?? null);
  readonly elementUuid = signal<string>('');
  readonly roles = signal<ReadonlySet<string>>(new Set());
  readonly token = signal<string | null>(null);

  constructor() {
    // Async seed: only set the token if refresh() hasn't already written a
    // fresher value while the promise was in flight.
    void this._auth.getAppToken().then((t) => {
      if (this.token() === null) this.token.set(t);
    });
    // tenantId is decoded from the app-token JWT on APP_TOKEN_UPDATED, which can
    // land after init. Keep tenantId/token in sync whenever the token is (re)issued
    // so the WebSocket can connect once a real tenant is known.
    this._bridge.appTokenUpdated$.pipe(takeUntilDestroyed()).subscribe(() => this.refresh());
  }

  correlationId(): string {
    return crypto.randomUUID();
  }

  refresh(): void {
    const tid = this._auth.getTenantId();
    if (tid) this.tenantId.set(Number(tid));
    this.tenantUuid.set(this._auth.getTenantUuid());
    this.token.set(this._auth.getAppTokenSync());
  }
}
